/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { observer } from "mobx-react";
import styled from "styled-components";
import { Link } from "react-router";
import {
  ArrowLeft,
  ArrowRightLeft,
  Bot,
  Cloud,
  FolderKanban,
  LayoutGrid,
  Rocket,
  Server,
  UserRound,
  type LucideIcon,
} from "lucide-react";

import { Pill, StatePill } from "@src/components/ui/Lists/ListTable/ListTable";
import ActionDropdown from "@src/components/ui/Dropdowns/ActionDropdown";
import type { DropdownAction } from "@src/components/ui/Dropdowns/ActionDropdown";
import IconTile, { type IconTileTone } from "@src/components/ui/IconTile/IconTile";
import shellStore from "@src/components/modules/NavigationModule/Navigation/shellStore";
import { itemFor } from "@src/components/modules/NavigationModule/Navigation/sections";

import { ThemePalette } from "@src/components/Theme";

/*
 * A details page's header, as the Ops Console's hub title row, on a white card: a back button to
 * the list, the item type's icon tile, the item's name with its type and status pills (and a line
 * of description), and the Actions menu as a secondary button on the right. The item's name also
 * becomes the last step of the top bar's breadcrumbs.
 */
const TYPE_ICONS: Record<string, { icon: LucideIcon; tone?: IconTileTone }> = {
  transfers: { icon: ArrowRightLeft, tone: "info" },
  deployments: { icon: Rocket },
  endpoints: { icon: Cloud },
  "minion-pools": { icon: Bot },
  "bare-metal-servers": { icon: Server },
  users: { icon: UserRound },
  projects: { icon: FolderKanban },
};
// Pill wording: Swift's names, never the upstream ones.
const TYPE_LABELS: Record<string, string> = {
  "minion pool": "SwiftIQ Agent",
  "minion-pool": "SwiftIQ Agent",
  server: "On-prem server",
  "bare metal server": "On-prem server",
};

const Wrapper = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 72px;
  padding: 16px 20px;
  box-sizing: border-box;
  background: var(--t9-bg-surface); /* Ops Console card */
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 8px;
  /* A phone: the actions wrap under the title, so the name stays readable. */
  @media (max-width: 720px) {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
  }
`;
const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  box-sizing: border-box;
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 6px;
  background: var(--t9-bg-surface);
  color: ${ThemePalette.grayscale[4]};
  cursor: pointer;
  &:hover {
    background: ${ThemePalette.grayscale[7]};
    color: ${ThemePalette.black};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
const TypeImage = styled.div<any>`
  min-width: 44px;
  height: 44px;
  background: url("${props => props.image}") no-repeat center;
  background-size: contain;
`;
const Title = styled.div<any>`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
  @media (max-width: 720px) {
    flex-basis: 100%;
  }
`;
const Status = styled.div<any>`
  flex-grow: 1;
  min-width: 0;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  @media (max-width: 720px) {
    flex-wrap: wrap;
    gap: 4px 8px;
  }
`;
const Text = styled.h1<any>`
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: ${ThemePalette.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  @media (max-width: 720px) {
    font-size: 17px;
    line-height: 24px;
    flex-basis: 100%;
  }
`;
const StatusPills = styled.div<any>`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;
const Description = styled.div<any>`
  margin-top: 2px;
  font-size: 14px;
  line-height: 20px;
  color: ${ThemePalette.grayscale[5]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const MockButton = styled.div<any>`
  width: 0;
`;
// ActionDropdown, drawn as an Ops secondary button (white, slate border) instead of a filled one.
const Actions = styled.div`
  flex-shrink: 0;
  & > div > div:first-child {
    width: auto;
    min-width: 128px;
    height: 34px;
    box-sizing: border-box;
    background: var(--t9-bg-surface);
    border-color: ${ThemePalette.grayscale[2]};
  }
  & > div > div:first-child:hover {
    background: ${ThemePalette.grayscale[7]};
  }
  & > div > div:first-child > div:first-child,
  & > div > div:first-child:hover > div:first-child {
    color: var(--t9-text-body); /* --t9-slate-700 */
    font-weight: 500;
  }
  & > div > div:first-child #dropdown-arrow-image,
  & > div > div:first-child:hover #dropdown-arrow-image {
    stroke: ${ThemePalette.grayscale[4]};
  }
`;

type Props = {
  dropdownActions?: DropdownAction[];
  backLink: string;
  /** The type's old illustration: only shown for a list without an icon of its own. */
  typeImage?: string;
  alertInfoPill?: boolean;
  primaryInfoPill?: boolean;
  statusPill?: string;
  statusLabel?: string;
  itemTitle?: string | null;
  itemType?: string;
  itemDescription?: string;
  largeDropdownActionItems?: boolean;
};
@observer
class DetailsContentHeader extends React.Component<Props> {
  componentDidMount() {
    shellStore.setItemTitle(this.props.itemTitle);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.itemTitle !== this.props.itemTitle) {
      shellStore.setItemTitle(this.props.itemTitle);
    }
  }

  componentWillUnmount() {
    shellStore.setItemTitle(null);
  }

  get listKey() {
    return (this.props.backLink || "").split("/").filter(Boolean)[0] || "";
  }

  renderTypeIcon() {
    const type = TYPE_ICONS[this.listKey];
    if (type) {
      return (
        <IconTile
          icon={type.icon}
          tone={type.tone}
          size={44}
          title={this.props.itemType ? this.typeLabel : undefined}
        />
      );
    }
    if (this.props.typeImage) {
      return <TypeImage image={this.props.typeImage} />;
    }
    return <IconTile icon={LayoutGrid} size={44} />;
  }

  get typeLabel() {
    const type = this.props.itemType || "";
    // Was "TYPE_LABELS[…] || type": the raw lower-case type ("replica", "migration") showed in the pill.
    const label = TYPE_LABELS[type.toLowerCase()] || type;
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  renderStatusPill() {
    if (!this.props.statusPill) {
      return null;
    }
    return (
      <StatusPills>
        {/* The same sentence-case badges as the list tables (Ops Console pills). */}
        <Pill tone={this.props.alertInfoPill ? "info" : "brand"}>{this.typeLabel}</Pill>
        <StatePill status={this.props.statusPill} label={this.props.statusLabel || undefined} />
      </StatusPills>
    );
  }

  renderButton() {
    if (!this.props.dropdownActions) {
      return <MockButton />;
    }

    return (
      <Actions>
        <ActionDropdown
          actions={this.props.dropdownActions}
          largeItems={this.props.largeDropdownActionItems}
        />
      </Actions>
    );
  }

  renderDescription() {
    if (!this.props.itemDescription) {
      return null;
    }

    return (
      <Description title={this.props.itemDescription}>{this.props.itemDescription}</Description>
    );
  }

  render() {
    const list = itemFor(this.listKey);
    return (
      <Wrapper>
        <BackButton
          to={this.props.backLink}
          aria-label={list ? `Back to ${list.label}` : "Back"}
          title={list ? `Back to ${list.label}` : "Back"}
        >
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
        </BackButton>
        {this.renderTypeIcon()}
        <Title>
          <Status>
            <TitleRow>
              <Text title={this.props.itemTitle}>{this.props.itemTitle}</Text>
              {this.renderStatusPill()}
            </TitleRow>
            {this.renderDescription()}
          </Status>
          {this.renderButton()}
        </Title>
      </Wrapper>
    );
  }
}

export default DetailsContentHeader;
